type Point = [number, number];
type CubicSegment = [Point, Point, Point];

function fmt(n: number) {
  if (Math.abs(n) < 0.000001) return "0";
  return n.toFixed(8).replace(/\.?0+$/, "");
}

function createCubicPath(start: Point, segments: CubicSegment[]) {
  let d = `M${fmt(start[0])} ${fmt(start[1])}`;
  for (const [cp1, cp2, end] of segments) {
    d += `C${fmt(cp1[0])} ${fmt(cp1[1])},${fmt(cp2[0])} ${fmt(cp2[1])},${fmt(end[0])} ${fmt(end[1])}`;
  }
  return d + "Z";
}

function translateX(segments: CubicSegment[], offset: number): CubicSegment[] {
  return segments.map(([cp1, cp2, end]) => [
    [cp1[0] + offset, cp1[1]],
    [cp2[0] + offset, cp2[1]],
    [end[0] + offset, end[1]],
  ]);
}

function scaleAround(
  start: Point,
  segments: CubicSegment[],
  sx: number,
  sy: number,
  ox: number,
  oy: number,
): { start: Point; segments: CubicSegment[] } {
  const s = (p: Point): Point => [ox + (p[0] - ox) * sx, oy + (p[1] - oy) * sy];
  return {
    start: s(start),
    segments: segments.map(([cp1, cp2, end]) => [s(cp1), s(cp2), s(end)]),
  };
}

const PAUSE_BAR_START: Point = [0, 1.33333];
const PAUSE_BAR_SEGMENTS: CubicSegment[] = [
  [
    [0, 0.596954],
    [0.596954, 0],
    [1.33333, 0],
  ],
  [
    [1.77778, 0],
    [2.22222, 0],
    [2.66667, 0],
  ],
  [
    [3.40305, 0],
    [4, 0.596954],
    [4, 1.33333],
  ],
  [
    [4, 4.44445],
    [4, 7.55558],
    [4, 10.6667],
  ],
  [
    [4, 11.403],
    [3.40305, 12],
    [2.66667, 12],
  ],
  [
    [2.22222, 12],
    [1.77778, 12],
    [1.33333, 12],
  ],
  [
    [0.596954, 12],
    [0, 11.403],
    [0, 10.6667],
  ],
  [
    [0, 7.55558],
    [0, 4.44445],
    [0, 1.33333],
  ],
];

const PLAY_START: Point = [0, 1.50224];
const PLAY_SEGMENTS: CubicSegment[] = [
  [
    [0, 0.347535],
    [1.25, -0.374151],
    [2.25, 0.2032],
  ],
  [
    [4.75, 1.64658],
    [7.25, 3.08995],
    [9.75, 4.53333],
  ],
  [
    [9.89001, 4.61416],
    [10.01041, 4.71197],
    [10.11122, 4.822],
  ],
  [
    [10.62959, 5.38784],
    [10.62959, 6.27689],
    [10.11122, 6.84273],
  ],
  [
    [10.01041, 6.95276],
    [9.89001, 7.05057],
    [9.75, 7.1314],
  ],
  [
    [7.25, 8.57477],
    [4.75, 10.01813],
    [2.25, 11.4615],
  ],
  [
    [1.25, 12.0389],
    [0, 11.3172],
    [0, 10.1625],
  ],
  [
    [0, 7.27575],
    [0, 4.38899],
    [0, 1.50224],
  ],
];

import type { PreviewPlayerSize } from "./previewPlayerSizeConfig";
import { PREVIEW_PLAYER_SIZE_CONFIG } from "./previewPlayerSizeConfig";

const PLAY_X_OFFSET = 0.75;

function getPlayPath() {
  const translatedStart: Point = [PLAY_START[0] + PLAY_X_OFFSET, PLAY_START[1]];
  const translatedSegments = translateX(PLAY_SEGMENTS, PLAY_X_OFFSET);
  return createCubicPath(translatedStart, translatedSegments);
}

function getPauseBarPath(xOffset: number) {
  const start: Point = [PAUSE_BAR_START[0] + xOffset, PAUSE_BAR_START[1]];
  const segments = translateX(PAUSE_BAR_SEGMENTS, xOffset);
  return createCubicPath(start, segments);
}

function getCollapsedPauseBarPath() {
  const { start, segments } = scaleAround(PAUSE_BAR_START, PAUSE_BAR_SEGMENTS, 0.001, 0.84, 4, 6);
  return createCubicPath(start, segments);
}

const playTrianglePath = getPlayPath();
const leftPauseBarPath = getPauseBarPath(0);
const rightPauseBarPath = getPauseBarPath(6.66666);
const collapsedLeftBarPath = getCollapsedPauseBarPath();

interface PreviewPlayerIconProps {
  isActive: boolean;
  size?: PreviewPlayerSize;
}

export function PreviewPlayerIcon({ isActive, size = "md" }: PreviewPlayerIconProps) {
  return (
    <svg aria-hidden className={`${PREVIEW_PLAYER_SIZE_CONFIG[size].icon} tw-shrink-0`} fill='none' viewBox='0 0 11 12'>
      <path d={isActive ? rightPauseBarPath : playTrianglePath} fill='currentColor' />
      <path d={isActive ? leftPauseBarPath : collapsedLeftBarPath} fill='currentColor' />
    </svg>
  );
}
